/*
 * Tailwind colour class → the colour string an SVG attribute needs.
 *
 * Recharts renders SVG and takes fill/stroke as plain strings; it cannot consume
 * "text-emerald-500". The markup's charts express every colour as a Tailwind
 * class, and the pages that call them pass tones straight through from
 * lib/tokens.js, so rather than duplicating hex values here we read the CSS
 * custom property Tailwind already generated. Tailwind stays the single source
 * of truth and the two cannot drift.
 *
 *   toneColor("text-emerald-500")            → "oklch(0.696 0.17 162.48)"
 *   toneColor("bg-brand-500")                → the brand-500 value
 *   gradientStops("from-brand-500 to-brand-400") → [from, to]
 *
 * WHY THIS CAN FAIL, AND WHY IT SHOUTS: Tailwind v4 only emits the theme
 * variables for classes it actually finds in the source text. A tone assembled
 * at runtime (`text-${colour}-500`) is never found, so its variable is never
 * emitted and this would resolve to "" — a chart that silently renders in the
 * fallback colour. Same trap as the interpolated class names called out in
 * lib/tokens.js. Every tone must be a complete string literal, and a miss warns
 * loudly in development.
 */

const PREFIXES = ["text-", "bg-", "from-", "to-", "via-", "stroke-", "fill-", "border-"];

// Resolved values are stable for the life of the page: the palette does not
// change with the theme (emerald-500 is emerald-500 in both), only which colour
// a component chooses does.
const cache = new Map();
const warned = new Set();

/** Already a colour rather than a class? Pass it straight through. */
const isLiteralColour = (value) =>
    /^(#|rgb|hsl|oklch|oklab|lab|lch|color\(|currentColor$|transparent$|none$)/i.test(value);

function readVariable(name) {
    if (typeof window === "undefined") return "";
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * @param {string} tone     e.g. "text-emerald-500", "bg-brand-500", "#0f0"
 * @param {string} fallback used when the tone is empty or cannot be resolved
 */
export function toneColor(tone, fallback = "currentColor") {
    if (!tone) return fallback;

    const first = String(tone).trim().split(/\s+/)[0];
    if (isLiteralColour(first)) return first;

    if (cache.has(first)) return cache.get(first);

    const prefix = PREFIXES.find((p) => first.startsWith(p));
    const name = prefix ? first.slice(prefix.length) : first;
    const value = readVariable(`--color-${name}`);

    if (!value && import.meta.env.DEV && !warned.has(first)) {
        warned.add(first);
        console.warn(
            `chart tone "${first}" resolved to nothing — is --color-${name} defined, ` +
                `and is the class written as a complete literal somewhere Tailwind can scan?`,
        );
    }

    const resolved = value || fallback;
    // Only cache real hits: a miss before the stylesheet is applied should be
    // retried rather than frozen in.
    if (value) cache.set(first, resolved);
    return resolved;
}

/**
 * "from-brand-500 to-brand-400" → ["<from>", "<to>"].
 *
 * A single class (or one with no `to-`) gives the same colour twice, which
 * renders as a flat fill — the sensible reading of "one colour".
 */
export function gradientStops(tone, fallback = "currentColor") {
    const parts = String(tone || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    const from = parts.find((p) => p.startsWith("from-")) || parts[0];
    const to = parts.find((p) => p.startsWith("to-")) || from;

    return [toneColor(from, fallback), toneColor(to, fallback)];
}

/**
 * Round a maximum up to something that makes a readable axis.
 * Ported verbatim from html-markup/assets/js/charts.js so the React charts pick
 * the same axis top as the markup they are replacing.
 */
export function niceMax(value) {
    if (value <= 0) return 1;
    const mag = 10 ** Math.floor(Math.log10(value));
    const norm = value / mag;
    const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
    return step * mag;
}

/** A DOM-id-safe token for a tone, so gradient ids stay readable. */
export function toneKey(tone) {
    return String(tone || "default").replace(/[^a-zA-Z0-9]+/g, "-");
}

/*
 * A top for an axis measured in HOURS.
 *
 * The generic niceMax rounds to a nice DECIMAL, which on a time axis produces
 * ticks like 1.88h and 38m — honest, since the gridlines really are there, but
 * nobody reads a day in 1.88-hour steps. These candidates all divide into four
 * parts a reader already thinks in: quarter hours, half hours, whole hours.
 */
const HOUR_TOPS = [0.5, 1, 2, 3, 4, 6, 8, 10, 12, 16, 20, 24];

export function niceHourMax(maxHours) {
    const value = Number(maxHours) || 0;
    return HOUR_TOPS.find((t) => t >= value) ?? Math.ceil(value / 12) * 12;
}
